import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { startVersionCheck } from './services/versionCheck';
import { ThemeProvider, createTheme, CssBaseline, Alert, Button, Box } from '@mui/material';
import { AuthProvider } from './context/AuthContext';
import { OrdersProvider } from './context/OrdersContext';
import LoginForm from './components/LoginForm';
import OrdersBoard from './components/OrdersBoard';
import ProtectedRoute from './utils/ProtectedRoute';

const UPDATE_COUNTDOWN_SEC = 30;

// Создание темы Material-UI
const theme = createTheme({
  palette: {
    primary: {
      main: '#6F4E37', // Кофейный цвет
    },
    secondary: {
      main: '#D4A574', // Светло-коричневый
    },
    success: {
      main: '#4caf50',
    },
    error: {
      main: '#f44336',
    },
    warning: {
      main: '#ff9800',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
  },
});

function App() {
  const [updatePrompt, setUpdatePrompt] = useState(/** @type { { countdown: number } | null } */ (null));

  useEffect(() => {
    return startVersionCheck({
      onNewVersion: () => setUpdatePrompt({ countdown: UPDATE_COUNTDOWN_SEC }),
    });
  }, []);

  const showUpdatePrompt = Boolean(updatePrompt);
  useEffect(() => {
    if (!showUpdatePrompt) return;
    const id = setInterval(() => {
      setUpdatePrompt((prev) => {
        if (!prev || prev.countdown <= 1) {
          window.location.reload();
          return null;
        }
        return { countdown: prev.countdown - 1 };
      });
    }, 1000);
    return () => clearInterval(id);
  }, [showUpdatePrompt]);

  const handleRefreshNow = () => window.location.reload();

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {updatePrompt && (
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={handleRefreshNow}>
              Обновить сейчас
            </Button>
          }
          sx={{ borderRadius: 0, alignItems: 'center' }}
        >
          <Box component="span">
            Вышла новая версия приложения. Обновите страницу для продолжения работы.
            {updatePrompt.countdown > 0 && (
              <> Автообновление через {updatePrompt.countdown} с.</>
            )}
          </Box>
        </Alert>
      )}
      <AuthProvider>
        <OrdersProvider>
          <Router>
            <Routes>
              <Route path="/login" element={<LoginForm />} />
              <Route
                path="/orders"
                element={
                  <ProtectedRoute>
                    <OrdersBoard />
                  </ProtectedRoute>
                }
              />
              <Route path="/" element={<Navigate to="/orders" replace />} />
              <Route path="*" element={<Navigate to="/orders" replace />} />
            </Routes>
          </Router>
        </OrdersProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
